/**
 * Budget date helpers
 *
 * All Budget service date/times are numeric yyyyMMddHHmmss stamps
 * (e.g. 20260704120000), already localized server-side. Formatting
 * delegates to the shared wall-clock helpers in `@/utils/dateTime`;
 * this module adds the stamp <-> Date round-trip needed by date inputs.
 */

import { ccmsWallClock, formatDate, formatDateTime } from '@/utils/dateTime';

const pad = (n: number) => String(n).padStart(2, '0');

/** Date -> yyyyMMddHHmmss numeric stamp (local wall-clock). */
export function toStamp(date: Date): number {
  return parseInt(
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}${pad(date.getHours())}${pad(
      date.getMinutes(),
    )}${pad(date.getSeconds())}`,
    10,
  );
}

/** Current moment as a yyyyMMddHHmmss numeric stamp. */
export function nowStamp(): number {
  return toStamp(new Date());
}

/** yyyyMMddHHmmss stamp -> Date (wall-clock verbatim), or null. */
export function stampToDate(stamp: number | null | undefined): Date | null {
  if (!stamp) return null;
  const d = ccmsWallClock(stamp).toDate();
  return isNaN(d.getTime()) ? null : d;
}

/** yyyyMMddHHmmss stamp -> "MMM D, YYYY" (or '-' when missing). */
export function formatStamp(stamp: number | null | undefined): string {
  if (!stamp) return '-';
  return formatDate(stamp);
}

/** yyyyMMddHHmmss stamp -> "MMM D, YYYY, hh:mm A" (or '-' when missing). */
export function formatStampDateTime(stamp: number | null | undefined): string {
  if (!stamp) return '-';
  return formatDateTime(stamp);
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** yyyyMMddHHmmss stamp -> "Jul 2026" (release schedule months), or '-'. */
export function formatStampMonth(stamp: number | null | undefined): string {
  if (!stamp) return '-';
  const year = Math.floor(stamp / 1e10);
  const month = Math.floor(stamp / 1e8) % 100;
  if (month < 1 || month > 12) return '-';
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

/** Date -> yyyyMM01000000 stamp (start of that month) for release rows. */
export function toMonthStartStamp(date: Date): number {
  return date.getFullYear() * 1e10 + (date.getMonth() + 1) * 1e8 + 1e6;
}

/** Currency code -> the symbol people actually read (falls back to the code itself). */
const CURRENCY_SYMBOLS: Record<string, string> = {
  ZAR: 'R ',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

export function currencySymbol(currencyCode?: string | null): string {
  const code = (currencyCode || 'ZAR').toUpperCase();
  return CURRENCY_SYMBOLS[code] ?? `${code} `;
}

/**
 * The single money formatter for Budget Management — "R 250,000" / "R 1,234.56".
 * Cents are dropped for whole amounts (budgets are read at a glance; the noise of
 * ".00" on every figure hurts scanning) and kept when they carry information.
 */
export function formatMoney(amount: number | null | undefined, currencyCode?: string | null): string {
  const value = amount ?? 0;
  const hasCents = Math.abs(value % 1) > 0.004;
  const body = Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
  const sign = value < 0 ? '-' : '';
  return `${sign}${currencySymbol(currencyCode)}${body}`;
}

/** Axis/label money: "R 250k" / "R 1.2m" — for charts where full figures crowd out the data. */
export function formatMoneyCompact(amount: number | null | undefined, currencyCode?: string | null): string {
  const value = amount ?? 0;
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const sym = currencySymbol(currencyCode);
  if (abs >= 1_000_000) return `${sign}${sym}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}m`;
  if (abs >= 1_000) return `${sign}${sym}${(abs / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return `${sign}${sym}${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/** Mantine colour for a consumed percentage — the same bands the backend's health lookup uses. */
export function consumptionColor(consumedPercent: number | null | undefined): string {
  const pct = consumedPercent ?? 0;
  if (pct > 100) return 'red';
  if (pct >= 90) return 'orange';
  if (pct >= 75) return 'yellow';
  return 'teal';
}

/**
 * Health band as a themed `gradientVariant` for the shared <Progress/>.
 * The wrapper paints a gradient over the bar, which overrides any `color` prop —
 * so consumption bars MUST select their band through this, or every bar renders
 * the same primary purple regardless of how consumed the budget is.
 */
export function consumptionGradient(
  consumedPercent: number | null | undefined
): 'success' | 'warning' | 'danger' {
  const pct = consumedPercent ?? 0;
  if (pct >= 90) return 'danger';
  if (pct >= 75) return 'warning';
  return 'success';
}
